import React, { useState, useEffect } from 'react';

interface KeywordEditModalProps {
  isOpen: boolean;
  keywords: string[];
  onSave: (newKeywords: string[]) => void;
  onClose: () => void;
  isDarkMode: boolean;
}

export const KeywordEditModal: React.FC<KeywordEditModalProps> = ({
  isOpen,
  keywords,
  onSave,
  onClose,
  isDarkMode
}) => {
  const [editedKeywords, setEditedKeywords] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setEditedKeywords([...keywords]);
    }
  }, [isOpen, keywords]);

  const updateKeyword = (index: number, value: string) => {
    const newKeywords = [...editedKeywords];
    newKeywords[index] = value.trim();
    setEditedKeywords(newKeywords);
  };

  const handleSave = () => {
    // 빈 키워드 제거 및 최대 3개로 제한
    const filteredKeywords = editedKeywords
      .filter(keyword => keyword.trim().length > 0)
      .slice(0, 3);
    
    // 3개가 안 되면 빈 문자열로 채우기
    while (filteredKeywords.length < 3) {
      filteredKeywords.push('');
    }
    
    onSave(filteredKeywords.filter(k => k.length > 0));
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) {return null;}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`relative w-96 max-w-md mx-4 rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      }`}>
        {/* 헤더 */}
        <div className={`px-6 py-4 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h3 className="text-lg font-semibold">
            키워드 편집
          </h3>
          <p className={`text-sm mt-1 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            이미지 검색에 사용될 영어 키워드 3개를 입력하세요
          </p>
        </div>

        {/* 내용 */}
        <div className="px-6 py-4 space-y-3">
          {[0, 1, 2].map((index) => (
            <div key={index}>
              <label className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                키워드 {index + 1}
              </label>
              <input
                type="text"
                value={editedKeywords[index] || ''}
                onChange={(e) => updateKeyword(index, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`예: ${index === 0 ? 'business' : index === 1 ? 'strategy' : 'growth'}`}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                maxLength={20}
              />
            </div>
          ))}
        </div>

        {/* 푸터 */}
        <div className={`px-6 py-4 border-t flex justify-end space-x-3 ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              isDarkMode
                ? 'text-gray-300 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-50'
            } transition-colors`}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
          >
            저장
          </button>
        </div>

        {/* 키보드 단축키 안내 */}
        <div className={`px-6 pb-4 text-xs ${
          isDarkMode ? 'text-gray-500' : 'text-gray-400'
        }`}>
          <span>Ctrl/Cmd + Enter: 저장 | Esc: 취소</span>
        </div>
      </div>
    </div>
  );
};